import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { RobuxIcon, useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Send, Users, MessageCircle, RefreshCw, Youtube, Crown, ShieldCheck, Shield, Tv, Music, Timer, Droplet, X, Lock, Flame, Smile, Search, Gem, Gavel, Bomb, Pin, PinOff, VolumeX, Volume2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
// 🛡️ hCaptcha
import HCaptcha from "@hcaptcha/react-hcaptcha";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const parseAmount = (text) => {
    const match = String(text).match(/[\$]?([\d,.]+)\s*([kmbKMB]?)/);
    if (!match) return 0;
    let value = parseFloat(match[1].replace(/,/g, ''));
    const suffix = match[2].toLowerCase();
    if (suffix === 'k') value *= 1000;
    else if (suffix === 'm') value *= 1000000;
    else if (suffix === 'b') value *= 1000000000;
    return Math.floor(value);
};

const formatCompact = (number) => {
  if (number === undefined || number === null) return "0";
  return new Intl.NumberFormat('en-US', { notation: "compact", maximumFractionDigits: 2 }).format(number);
};

const formatAmount = (num) => {
  if (num === undefined || num === null) return "0";
  if (num >= 1000000) return num.toLocaleString();
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

// ----- CHAT EMOTES -----
const EMOTE_NAMES = [
  'KEKW', 'OMEGALUL', 'LULW', 'PepeLaugh',
  'Sadge', 'FeelsBadMan', 'peepoSad', 'COPIUM', 'HOPIUM', 'Bedge',
  'FeelsGoodMan', 'peepoHappy', 'peepoLove', 'peepoShy',
  'pog', 'PogU', 'GIGACHAD', 'EZ', 'NOTED', 'peepoClap',
  'monkaS', 'monkaW', 'Susge', 'Clueless',
  'Madge', 'RAGEY', 'Smadge',
  'peepoArrive', 'peepoLeave',
  'Pepega', 'catJAM', 'Prayge', 'NOPERS', 'STONKS', 'NotStonks', 'PETTHEMODS', 'PRINGLE',
];

const EMOTE_URL_OVERRIDES = {};

const getEmoteUrl = (name) =>
  EMOTE_URL_OVERRIDES[name] || `/emotes/${name}.webp`;

const EMOTE_SET = new Set(EMOTE_NAMES);

// ==========================================
// MUTE STORE — tarayici cache'i (localStorage) destekli, sekmeler arasi senkron
// Muteleme tamamen client-side'dir: yalnizca bu cihazda mesajlari gizler.
// ==========================================
const MUTED_AVATAR = '/muted-avatar.png'; // public/muted-avatar.png
const MUTED_STORAGE_KEY = 'bloxbets:mutedUsers:v1';
const MUTED_EVENT = 'bloxbets:muted-changed';

const normalizeName = (u) => String(u || '').trim().toLowerCase();

const readMutedFromStorage = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(MUTED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(normalizeName).filter(Boolean) : [];
  } catch {
    return [];
  }
};

const writeMutedToStorage = (list) => {
  if (typeof window === 'undefined') return;
  const clean = Array.from(new Set(list.map(normalizeName).filter(Boolean)));
  try {
    window.localStorage.setItem(MUTED_STORAGE_KEY, JSON.stringify(clean));
  } catch {}
  // 'storage' eventi sadece DIGER sekmelerde tetiklenir; ayni sekmeyi de haberdar et.
  try {
    window.dispatchEvent(new CustomEvent(MUTED_EVENT, { detail: clean }));
  } catch {}
};

const muteStore = {
  list: () => readMutedFromStorage(),
  has: (username) => readMutedFromStorage().includes(normalizeName(username)),
  add: (username) => {
    const name = normalizeName(username);
    if (!name) return;
    writeMutedToStorage([...readMutedFromStorage(), name]);
  },
  remove: (username) => {
    const name = normalizeName(username);
    writeMutedToStorage(readMutedFromStorage().filter((n) => n !== name));
  },
  toggle: (username) => {
    if (muteStore.has(username)) muteStore.remove(username);
    else muteStore.add(username);
  },
};

// Mute listesini reaktif sekilde okuyan hook (sekmeler arasi + ayni sekme senkron)
const useMutedUsers = () => {
  const [mutedSet, setMutedSet] = useState(() => new Set(muteStore.list()));

  useEffect(() => {
    const sync = () => setMutedSet(new Set(muteStore.list()));
    const onStorage = (e) => { if (e.key === MUTED_STORAGE_KEY) sync(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener(MUTED_EVENT, sync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(MUTED_EVENT, sync);
    };
  }, []);

  const isMuted = useCallback((u) => mutedSet.has(normalizeName(u)), [mutedSet]);

  return {
    mutedSet,
    isMuted,
    mute: muteStore.add,
    unmute: muteStore.remove,
    toggle: muteStore.toggle,
  };
};

// ==========================================
// ODOMETER RAIN SAYACI
// ==========================================
const RollingDigit = ({ digit }) => {
  const d = parseInt(digit, 10);
  const offset = -((9 - d) * 10);
  return (
    <span className="relative inline-block overflow-hidden align-baseline" style={{ height: '1em', lineHeight: '1em' }}>
      <span className="invisible">0</span>
      <span
        className="absolute left-0 top-0 flex flex-col w-full transition-transform duration-[1000ms]"
        style={{ transform: `translateY(${offset}%)`, transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)' }}
      >
        {[9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map((n) => (
          <span key={n} className="flex items-center justify-center" style={{ height: '1em', lineHeight: '1em' }}>{n}</span>
        ))}
      </span>
    </span>
  );
};

const formatRainAmount = (v) => {
  v = Number(v) || 0;
  if (v >= 1000000) return v.toLocaleString();
  if (v >= 1000) return (v / 1000).toFixed(2).replace(/\.00$/, '') + 'K';
  return v.toFixed(2);
};

const renderRollingChars = (str) =>
  String(str).split('').map((ch, i) =>
    /\d/.test(ch)
      ? <RollingDigit key={`d-${i}`} digit={ch} />
      : <span key={`s-${i}`} className="inline-block" style={{ lineHeight: '1em' }}>{ch}</span>
  );

const OdometerAmount = ({ value, className = '', decimalClassName }) => {
  const text = formatRainAmount(value);
  const pureDecimal = /^\d+\.\d+$/.test(text); 
  if (pureDecimal && decimalClassName) {
    const [intP, decP] = text.split('.');
    return (
      <span className={`inline-flex items-start mm-num ${className}`} style={{ lineHeight: '1em' }}>
        {renderRollingChars(intP)}
        <span className={`inline-flex items-start mm-num ${decimalClassName}`} style={{ lineHeight: '1em' }}>
          .{renderRollingChars(decP)}
        </span>
      </span>
    );
  }
  return (
    <span className={`inline-flex items-end mm-num ${className}`} style={{ lineHeight: '1em' }}>
      {renderRollingChars(text)}
    </span>
  );
};

export const LiveChat = () => {
  const { user, token, isAuthenticated, wallet } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onlineCount, setOnlineCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [sending, setSending] = useState(false);
  const [isChatStopped, setIsChatStopped] = useState(false);

  // 📌 Sabitlenmiş sistem mesajı (pin). Site açıldığında açık gelir, pin'e basınca kapanır.
  const [systemPin, setSystemPin] = useState(null);
  const [systemPinOpen, setSystemPinOpen] = useState(true);
  
  const [rainActive, setRainActive] = useState(false);
  const [rainTimeLeft, setRainTimeLeft] = useState(0);
  const [rainDuration, setRainDuration] = useState(1800);
  const [rainParticipants, setRainParticipants] = useState(0);
  const [rainPrize, setRainPrize] = useState(1000000);
  const [hasJoinedRain, setHasJoinedRain] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [personalRainWin, setPersonalRainWin] = useState(null);

  const [captchaToken, setCaptchaToken] = useState(null);

  const [isTipModalOpen, setIsTipModalOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState('');
  const [isTipping, setIsTipping] = useState(false);

  const [selectedProfile, setSelectedProfile] = useState(null);

  const [showPicker, setShowPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const [showRules, setShowRules] = useState(false);

  const userIdRef = useRef(user?.id);
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user]);

  const wsRef = useRef(null);
  // Acik olan soket JWT ile mi (uye) yoksa guest id ile mi kuruldu?
  // Login'in hemen ardindan mesaj yollarken guest soketine dusmemek icin.
  const wsAuthedRef = useRef(false);
  const pollingRef = useRef(null);

  const inputRef = useRef(null);
  const pickerRef = useRef(null);
  const pickerButtonRef = useRef(null);

  const lastMessageIdRef = useRef(null);
  const chatContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);

  // Chat'i dibe sabitle (programatik).
  const scrollChatToBottom = useCallback(() => {
    const c = chatContainerRef.current;
    if (!c) return;
    c.scrollTop = c.scrollHeight;
    lastScrollTopRef.current = c.scrollTop;
  }, []);

  // Yeni mesajda dibe sabitle; gec yuklenen avatar/emote ve giris animasyonu
  // sebebiyle olusan kaymayi da birkac kez tekrar sabitleyerek yakala.
  const pinChatToBottomSoon = useCallback(() => {
    scrollChatToBottom();
    requestAnimationFrame(scrollChatToBottom);
    setTimeout(() => { if (isAtBottomRef.current) scrollChatToBottom(); }, 150);
    setTimeout(() => { if (isAtBottomRef.current) scrollChatToBottom(); }, 350);
  }, [scrollChatToBottom]);

  // ⚠️ KAYDIRMA BITISINI YAKALAYAN ZAMANLAYICI. Tarayicida "kaydirma bitti"
  //    diye bir olay yok; son `scroll` olayindan 120ms sonra bitmis sayiyoruz.
  //    Sure kisa tutuldu: uzun olsa efektler kaydirmadan sonra gec baslar ve
  //    kullanici "donmus" saniyor.
  const scrollIdleRef = useRef(null);

  const handleChatScroll = () => {
    const c = chatContainerRef.current;
    if (!c) return;

    // ⚠️ SINIF DOGRUDAN DOM'A YAZILIYOR, STATE'E DEGIL.
    //    setState kullanmak her kaydirma olayinda React'i yeniden render
    //    ettirirdi — cozmeye calistigimiz takilmayi bizzat uretirdi.
    if (!c.classList.contains('chat-scrolling')) {
      c.classList.add('chat-scrolling');
    }
    if (scrollIdleRef.current) clearTimeout(scrollIdleRef.current);
    scrollIdleRef.current = setTimeout(() => {
      const el = chatContainerRef.current;
      if (el) el.classList.remove('chat-scrolling');
    }, 120);

    const distanceFromBottom = c.scrollHeight - c.scrollTop - c.clientHeight;
    const atBottom = distanceFromBottom < 120;
    // Otomatik akisi YALNIZCA kullanici yukari kaydirinca durdur.
    // Resim/emote yuklenince icerik uzar ama scrollTop sabit kalir -> bunu
    // "yukari kaydirma" sayma; aksi halde chat oldugu yerde donar.
    const scrolledUp = c.scrollTop < lastScrollTopRef.current - 4;
    lastScrollTopRef.current = c.scrollTop;
    if (atBottom) {
      isAtBottomRef.current = true;
    } else if (scrolledUp) {
      isAtBottomRef.current = false;
    }
  };

  // Bilesen sokulurken bekleyen zamanlayiciyi birak — aksi halde
  // `chat-scrolling` sinifi asili kalabilir ve efektler geri baslamaz.
  useEffect(() => () => {
    if (scrollIdleRef.current) clearTimeout(scrollIdleRef.current);
  }, []);

  const formatRainTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const fetchRainState = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/rain/state`);
      const d = response.data || {};
      if (d.active !== undefined) setRainActive(d.active);
      if (d.time_remaining !== undefined && d.time_remaining !== null) setRainTimeLeft(d.time_remaining);
      if (d.participants_count !== undefined) setRainParticipants(d.participants_count);
      const prize = d.prize ?? d.pool ?? d.amount ?? d.rain_pool ?? d.total;
      if (prize !== undefined && prize !== null) setRainPrize(prize);
      const dur = d.duration ?? d.total_duration ?? d.cycle_duration;
      if (dur) setRainDuration(dur);
    } catch (error) {}
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/messages`);
      setMessages(response.data);
    } catch (error) {}
  }, []);

  // 📌 Sabitlenmiş sistem mesajını çek (sayfa ilk açıldığında pin'i göstermek için)
  const fetchSystemPin = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/system-message`);
      const msg = response.data?.message || null;
      setSystemPin(msg);
      if (msg) setSystemPinOpen(true); // ilk girişte açık
    } catch (error) {}
  }, []);

  const fetchOnlineCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/online`);
      setOnlineCount(response.data.count);
    } catch (error) {}
  }, []);

  useEffect(() => {
    let interval;
    if (rainTimeLeft > 0) {
      interval = setInterval(() => {
        setRainTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [rainTimeLeft]);

  useEffect(() => {
    fetchMessages();
    fetchOnlineCount();
    fetchRainState(); 
    fetchSystemPin();
    
    pollingRef.current = setInterval(() => {
      fetchOnlineCount();
      fetchRainState();
    }, 4000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [fetchMessages, fetchOnlineCount, fetchRainState, fetchSystemPin]);

  useEffect(() => {
    let wsUrl = 'ws://localhost:8000';
    if (BACKEND_URL && typeof BACKEND_URL === 'string') {
      wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    }

    let wsToken = token;
    const isAuthedSocket = !!token;
    if (!wsToken) {
      let storedGuestId = localStorage.getItem('casino_guest_id');
      if (!storedGuestId) {
        storedGuestId = 'guest_' + Math.random().toString(36).substring(2, 10);
        localStorage.setItem('casino_guest_id', storedGuestId);
      }
      wsToken = storedGuestId; 
    }
    
    let ws = null;
    let reconnectTimeout = null;
    let pingInterval = null;
    // 🔴 KRITIK: Bu effect temizlendi mi? (login/logout -> token degisir -> temizlenir)
    // Bu bayrak yokken, kapatilan ESKI soketin onclose'u 5 sn sonra ESKI kimlikle
    // yeniden baglaniyor ve wsRef.current'i eziyordu. Sonuc: login olmus kullanici
    // guest soket uzerinden mesaj yolluyor -> "You must log in to chat!".
    let disposed = false;

    const connect = () => {
      if (disposed) return;

      const socket = new WebSocket(`${wsUrl}/ws/chat/${wsToken}`);
      ws = socket;
      wsRef.current = socket;
      wsAuthedRef.current = isAuthedSocket;

      // Bu soket hala guncel soket mi? (eski effect'ten kalma olabilir)
      const isCurrent = () => !disposed && wsRef.current === socket;

      socket.onopen = () => {
        if (!isCurrent()) { try { socket.close(); } catch (err) {} return; }
        setConnected(true);
        fetchMessages();
        pingInterval = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) socket.send('ping');
        }, 25000);
      };

      socket.onmessage = (event) => {
        if (!isCurrent()) return;
        if (event.data === 'pong') return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'clear_user_messages') {
            setMessages(prevMessages => prevMessages.filter(msg => msg.user_id !== data.user_id));
          }
          else if (data.type === 'system_pin') {
            // 📌 Admin pin'i ayarladı/kaldırdı. Yeni mesaj geldiğinde otomatik aç.
            const msg = data.message || null;
            setSystemPin(msg);
            if (msg) setSystemPinOpen(true);
          }
          else if (data.type === 'chat_state') {
            setIsChatStopped(data.stopped);
          }
          if (data.type === 'chat_message') {
            setMessages(prev => {
              if (prev.some(m => m.id === data.id)) return prev;
              return [...prev.slice(-49), data];
            });
          } else if (data.type === 'online_count') {
            setOnlineCount(data.count);
          } else if (data.type === 'error') {
            toast.error(data.message);
          } else if (data.type === 'RAIN_START') {
            if (isAuthenticated) {
                setRainActive(true);
                setRainTimeLeft(data.duration);
                if (data.duration) setRainDuration(data.duration);
                setRainParticipants(0);
                if (data.prize) setRainPrize(data.prize);
                setHasJoinedRain(false);
                setCaptchaToken(null);
                setPersonalRainWin(null);
            }
          } else if (data.type === 'RAIN_TICK') {
            if (isAuthenticated) {
                setRainActive(true);
                setRainTimeLeft(data.time_left); 
                setRainParticipants(data.participants_count);
                if (data.prize) setRainPrize(data.prize);
            }
          } else if (data.type === 'RAIN_END') {
            setRainActive(false);
            setHasJoinedRain(false);
            setCaptchaToken(null);
            setTimeout(() => { fetchRainState(); }, 800);
            
            const currentUserId = userIdRef.current; 
            if (currentUserId && data.personal_amounts && data.personal_amounts[currentUserId]) {
                setPersonalRainWin(data.personal_amounts[currentUserId]);
                setTimeout(() => setPersonalRainWin(null), 15000);
            }
          }
        } catch (e) {}
      };

      socket.onclose = () => {
        if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
        // 🔴 Eski/olu soket ASLA kendini yeniden baglamaz.
        if (!isCurrent()) return;
        setConnected(false);
        reconnectTimeout = setTimeout(connect, 3000);
      };

      socket.onerror = () => { /* onclose zaten tetiklenecek */ };
    };

    connect();

    return () => {
      disposed = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (pingInterval) clearInterval(pingInterval);
      if (ws) {
        // Handler'lari sustur: kapanirken reconnect/state guncellemesi tetiklenmesin.
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        try { ws.close(); } catch (err) {}
      }
      if (wsRef.current === ws) {
        wsRef.current = null;
        wsAuthedRef.current = false;
      }
    };
  }, [token, isAuthenticated, fetchMessages]);

  useEffect(() => {
    if (messages.length > 0 && chatContainerRef.current) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.id !== lastMessageIdRef.current) {
        if (isAtBottomRef.current) {
          // Layout/commit sonrasi dibe sabitle (gec gelen resimleri de yakalar).
          requestAnimationFrame(() => pinChatToBottomSoon());
        }
        lastMessageIdRef.current = lastMessage.id;
      }
    }
  }, [messages, pinChatToBottomSoon]);

  useEffect(() => {
    if (!showPicker) return;
    const handleClickOutside = (e) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target) &&
        pickerButtonRef.current &&
        !pickerButtonRef.current.contains(e.target)
      ) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPicker]);

  useEffect(() => {
    if (!showPicker) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') setShowPicker(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showPicker]);

  const insertEmote = (name) => {
    const code = `:${name}:`;
    setNewMessage(prev => {
      const sep = prev && !prev.endsWith(' ') ? ' ' : '';
      return (prev + sep + code + ' ').slice(0, 200);
    });
    inputRef.current?.focus();
  };

  const sendMessageViaAPI = async (messageText) => {
    try {
      await axios.post(`${API}/chat/send`, { message: messageText }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchMessages();
      return true;
    } catch (error) { return false; }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !isAuthenticated || sending) return;

    const messageText = newMessage.trim();
    setSending(true);
    setNewMessage('');
    setShowPicker(false);
    setPickerSearch('');

    // 🔴 Soket guest kimligiyle aciksa (ornegin login'in hemen ardindan, yeni
    // soket daha kurulmadan) WS'e YOLLAMA -> sunucu "You must log in to chat!" doner.
    // Bu durumda HTTP fallback kullan: istek Bearer token ile gider, ilk denemede calisir.
    const canUseSocket =
      wsAuthedRef.current &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN;

    if (canUseSocket) {
      wsRef.current.send(JSON.stringify({ type: 'message', message: messageText }));
      setSending(false);
    } else {
      const success = await sendMessageViaAPI(messageText);
      if (!success) setNewMessage(messageText);
      setSending(false);
    }
  };

  const handleJoinRain = async () => {
    if (!isAuthenticated) return;
    if (!captchaToken) return toast.error("Please verify that you are not a robot!");

    setIsJoining(true);
    try {
      await axios.post(`${API}/chat/rain/join`, { captcha_token: captchaToken }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setHasJoinedRain(true);
      setIsJoinModalOpen(false);
      toast.success("Successfully joined the Rain Event!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join rain!");
      setCaptchaToken(null);
    } finally { setIsJoining(false); }
  };

  const handleTipSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
        return toast.error("Please log in to tip!");
    }
    const amount = parseAmount(tipAmount);

    if (amount < 10) {
      return toast.error("Minimum tip amount is 10 $!");
    }

    setIsTipping(true);
    try {
      // 🍀 RAIN HAVUZU TEK CÜZDANLIDIR (site parası). Backend, BB Coin
      // cüzdanındaki kullanıcıyı 400 ile reddeder: gerçek para, site parası
      // olarak rastgele kullanıcılara dağıtılamaz. Kullanıcı bunu istek
      // göndermeden ÖNCE öğrenmeli.
      if (wallet === 'lc') {
        return toast.error("Rain runs on the site balance. Switch to your site wallet to tip.");
      }
      await axios.post(`${API}/game/rain/tip`, { amount }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(`Successfully tipped ${formatAmount(amount)} $ to the Rain Pool!`);
      setIsTipModalOpen(false);
      setTipAmount('');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send tip.");
    } finally {
      setIsTipping(false);
    }
  };

  const handleUserClick = async (username) => {
    if (username === 'System' || username === 'SYSTEM' || username.includes('RAIN') || username.includes('RACE')) return; 
    
    setSelectedProfile({ username, loading: true });
    try {
      const response = await axios.get(`${API}/user/public-profile/${username}`);
      setSelectedProfile({ ...response.data, loading: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load profile.");
      setSelectedProfile(null);
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getLevelBadge = (level) => {
    const lvl = Number(level) || 1;
    if (lvl >= 150) return {
        bg: 'tnt-badge',
        text: (
            <span className="flex items-center justify-center gap-1">
                <Bomb className="w-3 h-3 text-[#2a0a00] drop-shadow-md animate-pulse" />
                Lv.{lvl}
            </span>
        )
    };
    if (lvl >= 120) return {
        bg: 'gold-badge',
        text: (
            <span className="flex items-center justify-center gap-1">
                <Crown className="w-3 h-3 text-[#3e2700] drop-shadow-md" /> 
                Lv.{lvl}
            </span>
        )
    };
    if (lvl >= 100) return {
        bg: 'fire-badge', 
        text: (
            <span className="flex items-center justify-center gap-1">
                <Flame className="w-3 h-3 text-yellow-200 fill-orange-500 animate-pulse" /> 
                Lv.{lvl}
            </span>
        ) 
    };
    
    if (lvl >= 75) return { bg: 'bg-gradient-to-r from-yellow-400 to-amber-500 text-amber-950 shadow-[0_0_10px_rgba(251,191,36,0.6)]', text: `Lv.${lvl}` };
    if (lvl >= 50) return { bg: 'bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-[0_0_8px_rgba(244,63,94,0.5)]', text: `Lv.${lvl}` };
    if (lvl >= 25) return { bg: 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white', text: `Lv.${lvl}` };
    if (lvl >= 10) return { bg: 'bg-gradient-to-r from-green-500 to-teal-500 text-white', text: `Lv.${lvl}` };
    return { bg: 'bg-zinc-700 text-zinc-200', text: `Lv.${lvl}` };
  };

  // Avatarı yüklenemeyen kullanıcılar. Mesaj BAŞINA değil KULLANICI başına
  // tutuluyor: aynı kişi 20 mesaj yazmışsa 20 kez denemenin anlamı yok.
  const [failedAvatars, setFailedAvatars] = useState(() => new Set());

  // ⚠️ SET HİÇ TEMİZLENMİYORDU — "profil fotoğrafım güncellenmiyor"un sebebi.
  // Kullanıcı Roblox'unu bağlamadan önce avatarı 404 veriyor ve adı bu sete
  // giriyor. Sonra Roblox'unu bağlıyor ama set'te olduğu için resim BİR DAHA
  // HİÇ denenmiyor; sayfayı yenilese bile tarayıcı önceki 404'ü önbellekten
  // veriyordu. Roblox adı değişince seti sıfırlıyoruz.
  useEffect(() => {
    setFailedAvatars(new Set());
  }, [user?.roblox_username]);

  // Kullanıcının KENDİ avatarı için sürüm damgası. URL `site_username`e bağlı
  // ve o değişmiyor; damga olmadan tarayıcı eski görseli (ya da eski 404'ü)
  // vermeye devam eder. Başkalarının Roblox adını bilmediğimiz için damga
  // yalnızca kendi satırlarımıza uygulanır.
  const avatarUrl = useCallback((uname) => {
    const base = `${API}/user/avatar/${encodeURIComponent(uname)}`;
    if (user?.site_username && uname === user.site_username) {
      return `${base}?v=${encodeURIComponent(user?.roblox_username || '')}`;
    }
    return base;
  }, [user?.site_username, user?.roblox_username]);

  const getAvatarColor = (username) => {
    const colors = [
      'from-purple-500 to-rose-500', 'from-purple-500 to-indigo-500',
      'from-blue-500 to-cyan-500', 'from-green-500 to-green-500',
      'from-orange-500 to-amber-500', 'from-red-500 to-purple-500',
    ];
    const index = username?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const renderEmotesInPart = (text, keyPrefix = 'e') => {
    if (!text || typeof text !== 'string' || EMOTE_NAMES.length === 0) return text;
    const regex = /:([A-Za-z0-9_]+):/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(text)) !== null) {
      const [full, name] = match;
      if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
      if (EMOTE_SET.has(name)) {
        parts.push(
          <img
            key={`${keyPrefix}-${key++}`}
            src={getEmoteUrl(name)}
            alt={name}
            title={name}
            className="inline-block h-7 align-middle mx-0.5"
            onError={(e) => { e.currentTarget.replaceWith(document.createTextNode(full)); }}
          />
        );
      } else {
        parts.push(full);
      }
      lastIndex = match.index + full.length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
  };

  const filteredEmotes = pickerSearch.trim()
    ? EMOTE_NAMES.filter(n => n.toLowerCase().includes(pickerSearch.trim().toLowerCase()))
    : EMOTE_NAMES;

  const visibleMessages = messages; // sistem mesajları (rain tip & mute) da görünür
  const myUsername = user?.site_username || user?.username || '';
  const { isMuted, mute, unmute } = useMutedUsers();

  return (
    <div className="h-full w-full flex flex-col bg-[#1A1D20] overflow-hidden" data-testid="live-chat">
      
      <style>{`
        /* Site temalı sohbet scrollbar'ı */
        .scrollbar-thin { scrollbar-width: thin; scrollbar-color: #3a3d43 #15171a; }
        .scrollbar-thin::-webkit-scrollbar { width: 7px; height: 7px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: #15171a; border-radius: 8px; }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #B059E8 0%, #7b2fc4 100%);
          border-radius: 8px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, #c98bf0 0%, #B059E8 100%); }

        /* ══════════════════════════════════════════════════════════════
           KAYDIRMA AKICILIGI
           ══════════════════════════════════════════════════════════════
           ⚠️ 'contain: content' — her mesaj satiri kendi boyama alanina
           KAPATILIYOR. Bu olmadan tarayici, bir mesajdaki efekt her
           karede degistiginde o degisikligin komsu satirlari etkileyip
           etkilemedigini hesaplamak zorunda kaliyor. Kapsama ile "bu
           satirin disina tasmaz" garantisi verilince tarayici komsulari
           hic sorgulamiyor.

           ⚠️ '.chat-scrolling' — KAYDIRMA SIRASINDA TUM EFEKTLER DURUR.
           Efektler ne kadar optimize edilirse edilsin, hizli kaydirmada
           tarayicinin ayni karede hem kaydirmayi hem N animasyonu
           yetistirmesi gerekiyor. Kaydirma bitene kadar (120ms) animasyon
           saati durduruluyor; birakinca kaldigi yerden devam ediyor.
           Kullanici kaydirirken zaten efektlere bakmiyor. */
        .chat-msg { contain: content; }
        .chat-scrolling .chat-msg *,
        .chat-scrolling .chat-msg *::before,
        .chat-scrolling .chat-msg *::after,
        .chat-scrolling .chat-msg::before,
        .chat-scrolling .chat-msg::after {
          animation-play-state: paused !important;
        }

        /* ⚠️ SURUKLENEN PARCACIKLARDA 'will-change' YOK.
           Her parcacik icin ayri bir GPU katmani ayirmak, 5 parcacik x N
           mesaj = onlarca katman demek. Chrome katman butcesini asinca
           katmanlari birlestirmeye baslar ve tam da kacinmak istedigimiz
           takilma olusur. 'transform' animasyonu zaten katman aliyor;
           el ile zorlamak fayda degil zarar veriyordu. */

        .fire-text {
          background: linear-gradient(to right, #ff0000, #ff5a00, #ff9a00, #ffce00, #ff0000);
          background-size: 200% auto;
          color: transparent !important;
          -webkit-background-clip: text !important;
          background-clip: text !important;
          animation: fire-anim 2s linear infinite;
          text-shadow: 0px 2px 10px rgba(255, 90, 0, 0.4);
          font-weight: 900;
        }
        .fire-badge {
          background: linear-gradient(to right, #ff0000, #ff5a00, #ff9a00, #ffce00, #ff0000) !important;
          background-size: 200% auto !important;
          color: white !important;
          animation: fire-anim 2s linear infinite !important;
          box-shadow: 0 0 15px rgba(255, 90, 0, 0.8), inset 0 0 10px rgba(255, 200, 0, 0.5) !important;
          border: 1px solid rgba(255, 200, 0, 0.8) !important;
        }
        /* ⚠️ box-shadow ANIMASYONU KALDIRILDI.
           box-shadow bilesik katmanda calismaz: her karede elemanin YENIDEN
           BOYANMASI gerekir. Ekranda 10-15 efektli mesaj varken bu, saniyede
           60 kez onlarca yeniden boyama demek ve kaydirma tam o anda takiliyor.
           Yerine sabit bir box-shadow + nabzi OPAKLIKLA veren bir ::after
           katmani kondu. opacity GPU'da calisir, boyama gerektirmez. */
        .fire-avatar-border {
          position: relative;
          border-color: #ff5a00 !important;
          box-shadow: 0 0 26px rgba(255, 90, 0, 0.7), inset 0 0 14px rgba(255, 90, 0, 0.45) !important;
        }
        .fire-avatar-border::after {
          content: ""; position: absolute; inset: -3px; border-radius: inherit;
          box-shadow: 0 0 40px rgba(255, 90, 0, 1), inset 0 0 20px rgba(255, 200, 0, 0.6);
          animation: glow-breathe 1.5s infinite alternate;
          pointer-events: none;
        }
        @keyframes fire-anim {
          to { background-position: 200% center; }
        }

        .gold-text {
          background: linear-gradient(to right, #e6c27a, #fff2cd, #d4af37, #fff2cd, #e6c27a);
          background-size: 200% auto;
          color: transparent !important;
          -webkit-background-clip: text !important;
          background-clip: text !important;
          animation: gold-anim 3s linear infinite;
          text-shadow: 0px 2px 10px rgba(212, 175, 55, 0.4);
          font-weight: 900;
        }
        .gold-badge {
          background: linear-gradient(to right, #d4af37, #fff2cd, #aa771c, #fff2cd, #d4af37) !important;
          background-size: 200% auto !important;
          color: #3e2700 !important;
          animation: gold-anim 3s linear infinite !important;
          box-shadow: 0 0 15px rgba(212, 175, 55, 0.8), inset 0 0 10px rgba(255, 242, 205, 0.5) !important;
          border: 1px solid rgba(255, 242, 205, 0.8) !important;
        }
        .gold-avatar-border {
          position: relative;
          border-color: #fff2cd !important;
          box-shadow: 0 0 26px rgba(212, 175, 55, 0.7), inset 0 0 14px rgba(212, 175, 55, 0.45) !important;
        }
        .gold-avatar-border::after {
          content: ""; position: absolute; inset: -3px; border-radius: inherit;
          box-shadow: 0 0 40px rgba(255, 242, 205, 1), inset 0 0 20px rgba(255, 242, 205, 0.6);
          animation: glow-breathe 1.5s infinite alternate;
          pointer-events: none;
        }
        /* AKAN GRADYAN transform ILE - background-position DEGIL.
           background-position animasyonu GPU'da birlestirilemez: her karede
           yeniden boyama gerekir. Kaydirma sirasinda tarayici hem kaydirmayi
           hem boyamayi yetistiremeyince arkaplan mesajdan KAYIYOR gibi
           gorunuyor ve kaydirma takiliyordu.
           Cozum: gradyan bir ::before katmanina alindi, translateX ile
           kaydiriliyor. transform GPU'da calisir; katman mesaj kutusuna
           BAGLI oldugu icin onunla birlikte hareket eder. */
        .gold-message-bg {
          position: relative;
          overflow: hidden;
          background: rgba(212, 175, 55, 0.12) !important;
          border: 1px solid rgba(212, 175, 55, 0.4) !important;
        }
        /* Nabiz artik ::after uzerinde opaklikla — box-shadow animasyonu YOK. */
        .gold-message-bg::after {
          content: ""; position: absolute; inset: 0; border-radius: inherit;
          box-shadow: 0 0 32px rgba(212, 175, 55, 0.35), inset 0 0 20px rgba(212, 175, 55, 0.2);
          animation: glow-breathe 2s infinite alternate;
          pointer-events: none; z-index: 0;
        }
        .gold-message-bg::before {
          content: "";
          position: absolute;
          top: 0; bottom: 0; left: 0;
          width: 200%;
          background: linear-gradient(to right,
            rgba(212, 175, 55, 0.10), rgba(255, 242, 205, 0.28),
            rgba(212, 175, 55, 0.10), rgba(255, 242, 205, 0.28),
            rgba(212, 175, 55, 0.10));
          animation: bg-slide 3s linear infinite;
          pointer-events: none;
          z-index: 0;
        }
        /* Icerik katmanin USTUNDE kalmali, yoksa gradyan metni orter. */
        .gold-message-bg > * { position: relative; z-index: 1; }
        @keyframes bg-slide { to { transform: translateX(-50%); } }
        /* TEK NABIZ TANIMI — dort efekt de bunu kullanir. Sadece opaklik
           degisir, yani bilesik katmanda kalir ve yeniden boyama olmaz. */
        @keyframes glow-breathe { from { opacity: 0.25; } to { opacity: 1; } }
        @keyframes gold-anim {
          to { background-position: 200% center; }
        }

        /* HIGH ROLLER (Lv.80+) — cyan/emerald diamond, distinct from fire & gold */
        /* ⚠️ 'filter' ANIMASYONU DA KALDIRILDI, box-shadow ile ayni gerekce:
           drop-shadow her karede yeniden hesaplanip boyaniyor. Parlama
           sabit birakildi, nabiz yalnizca scale ile veriliyor — transform
           bilesik katmanda calisir, boyama gerektirmez. */
        .highroller-gem {
          color: #5eead4;
          filter: drop-shadow(0 0 5px rgba(94,234,212,0.95)) drop-shadow(0 0 11px rgba(56,189,248,0.6));
          animation: hr-pulse 1.8s ease-in-out infinite;
        }
        @keyframes hr-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.12); }
        }

        /* ============================================ */
        /* 💣 TNT / EXPLOSIVE TIER (Lv.150+) — top tier  */
        /* ============================================ */
        .tnt-text {
          background: linear-gradient(100deg, #ffffff, #ffe9a8, #ffc400, #ff9d2f, #fff4d6, #ffc400, #ffffff);
          background-size: 250% auto;
          color: transparent !important;
          -webkit-background-clip: text !important;
          background-clip: text !important;
          animation: tnt-bg-shift 2.2s linear infinite;
          text-shadow: 0 1px 4px rgba(0,0,0,0.9), 0 0 14px rgba(255,160,0,0.45);
          font-weight: 900;
        }
        .tnt-badge {
          background: linear-gradient(100deg, #ff2200, #ff7a00, #ffd000, #ff7a00, #ff2200) !important;
          background-size: 250% auto !important;
          color: #2a0a00 !important;
          animation: tnt-bg-shift 2s linear infinite, tnt-badge-shake 2.6s ease-in-out infinite !important;
          box-shadow: 0 0 16px rgba(255, 60, 0, 0.9), inset 0 0 10px rgba(255, 210, 0, 0.6) !important;
          border: 1px solid rgba(255, 220, 120, 0.9) !important;
          font-weight: 900 !important;
        }
        .tnt-avatar-border {
          position: relative;
          border-color: #ffae00 !important;
          box-shadow: 0 0 26px rgba(255, 60, 0, 0.8), inset 0 0 14px rgba(255, 120, 0, 0.5) !important;
        }
        .tnt-avatar-border::after {
          content: ""; position: absolute; inset: -3px; border-radius: inherit;
          box-shadow: 0 0 42px rgba(255, 160, 0, 1), inset 0 0 22px rgba(255, 200, 0, 0.7);
          animation: glow-breathe 1.1s infinite alternate;
          pointer-events: none;
        }
        /* Altinla ayni gerekce: akan kisim transform'a tasindi. */
        .tnt-message-bg {
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(255, 90, 0, 0.55) !important;
          background:
            radial-gradient(circle at 50% 120%, rgba(255, 120, 0, 0.30) 0%, transparent 55%),
            rgba(60, 8, 0, 0.92) !important;
        }
        .tnt-message-bg::after {
          content: ""; position: absolute; inset: 0; border-radius: inherit;
          box-shadow: 0 0 40px rgba(255,140,0,0.7), inset 0 0 28px rgba(255,180,0,0.3);
          animation: glow-breathe 1.6s ease-in-out infinite alternate;
          pointer-events: none; z-index: 0;
        }
        .tnt-message-bg::before {
          content: "";
          position: absolute;
          top: 0; bottom: 0; left: 0;
          width: 200%;
          background: linear-gradient(110deg,
            rgba(60,8,0,0.92), rgba(120,15,0,0.85), rgba(40,5,0,0.92),
            rgba(120,15,0,0.85), rgba(60,8,0,0.92));
          animation: bg-slide 3s linear infinite;
          pointer-events: none;
          z-index: 0;
        }
        .tnt-message-bg > * { position: relative; z-index: 1; }
        @keyframes tnt-bg-shift { to { background-position: 250% center, 250% center; } }
        @keyframes tnt-badge-shake {
          0%, 90%, 100% { transform: rotate(0deg); }
          92% { transform: rotate(-5deg); }
          94% { transform: rotate(5deg); }
          96% { transform: rotate(-4deg); }
          98% { transform: rotate(3deg); }
        }

        /* TNT görsel katmanı: arka planda süzülüp kenarlardan sekerek
           dolaşan + kendi ekseninde dönen TNT'ler.
           Dış span yörüngeyi (translate), iç img dönmeyi (rotate) yapar
           — böylece iki hareket çakışmadan üst üste biner. */
        .tnt-decor {
          position: absolute; inset: 0; overflow: hidden;
          pointer-events: none; z-index: 0; border-radius: 0.75rem;
        }
        .tnt-float { position: absolute; }
        .tnt-float img {
          display: block; width: 100%; height: 100%;
          image-rendering: pixelated; user-select: none;
          filter: drop-shadow(0 0 8px rgba(255,110,0,0.85)) drop-shadow(0 0 2px rgba(0,0,0,0.6));
          animation: tnt-spin-cw 4.5s linear infinite;
        }
        .tnt-float.ccw img { animation: tnt-spin-ccw 6s linear infinite; }
        @keyframes tnt-spin-cw  { to { transform: rotate(360deg); } }
        @keyframes tnt-spin-ccw { to { transform: rotate(-360deg); } }

        /* her TNT: farklı boy / başlangıç / hız / yörünge (tam shorthand → her ortamda çalışır) */
        .tnt-f1 { width: 32px; height: 32px; top: 8px;  left: 4px;  opacity: .55; animation: tnt-drift-1 11s linear infinite; }
        .tnt-f2 { width: 26px; height: 26px; top: 26px; left: 30%; opacity: .45; animation: tnt-drift-2 14s linear infinite; }
        .tnt-f3 { width: 36px; height: 36px; top: 2px;  left: 55%; opacity: .42; animation: tnt-drift-3 16s linear infinite; }
        .tnt-f4 { width: 24px; height: 24px; top: 30px; left: 75%; opacity: .52; animation: tnt-drift-4 12s linear infinite; }
        .tnt-f5 { width: 30px; height: 30px; top: 14px; left: 88%; opacity: .40; animation: tnt-drift-3 18s linear infinite; }

        /* kutu içinde sekerek dolaşma yörüngeleri (köşelerde yön değiştirir) */
        @keyframes tnt-drift-1 {
          0%   { transform: translate(0, 0); }
          22%  { transform: translate(150px, 18px); }
          44%  { transform: translate(40px, -14px); }
          66%  { transform: translate(200px, 10px); }
          88%  { transform: translate(70px, 16px); }
          100% { transform: translate(0, 0); }
        }
        @keyframes tnt-drift-2 {
          0%   { transform: translate(0, 0); }
          25%  { transform: translate(-90px, -16px); }
          50%  { transform: translate(70px, 14px); }
          75%  { transform: translate(-50px, -10px); }
          100% { transform: translate(0, 0); }
        }
        @keyframes tnt-drift-3 {
          0%   { transform: translate(0, 0); }
          20%  { transform: translate(-120px, 16px); }
          40%  { transform: translate(-30px, -16px); }
          70%  { transform: translate(-180px, 8px); }
          100% { transform: translate(0, 0); }
        }
        @keyframes tnt-drift-4 {
          0%   { transform: translate(0, 0); }
          30%  { transform: translate(-150px, -14px); }
          55%  { transform: translate(-60px, 16px); }
          80%  { transform: translate(-200px, -8px); }
          100% { transform: translate(0, 0); }
        }
      `}</style>

      {/* ============================================ */}
      {/* MOBİL İÇİN ÜST HEADER VE KAPATMA BUTONU */}
      {/* ============================================ */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-[#2A2D31] bg-[#1f2226] shrink-0 z-40 shadow-sm">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-[#B059E8]" />
          <span className="font-heading font-black text-white tracking-wide text-sm">LIVE CHAT</span>
        </div>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('toggleMobileChat'))}
          className="p-1.5 rounded-lg bg-[#1A1D20] border border-[#2A2D31] text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* ============================================ */}
      {/* RAIN POOL */}
      {/* ============================================ */}

      {rainActive && rainTimeLeft <= 120 ? (
        <div className="relative flex-shrink-0 overflow-hidden z-30 px-3 pt-3">
          <div
            className="relative rounded-2xl border border-[#B059E8]/30 overflow-hidden px-4 pt-4 pb-5"
            style={{ background: 'radial-gradient(circle at 50% 15%, rgba(176,89,232,0.20) 0%, transparent 60%), linear-gradient(180deg, #221a2e 0%, #1a1622 100%)' }}
          >
            {/* Son 2 dakikada rain divinin etrafinda donen kesik cizgili border (marching ants) */}
            <style>{`
              @keyframes rain-dash-march { to { stroke-dashoffset: -48; } }
              .rain-dash-march { animation: rain-dash-march 1.1s linear infinite; }
            `}</style>
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="1.5" y="1.5" width="calc(100% - 3px)" height="calc(100% - 3px)" rx="15" ry="15" fill="none" stroke="url(#rain-border-gradient-chat)" strokeWidth="2" strokeDasharray="14 11" strokeLinecap="round" className="rain-dash-march" style={{ opacity: 0.9 }} />
              <defs>
                <linearGradient id="rain-border-gradient-chat" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#d29bff" />
                  <stop offset="50%" stopColor="#B059E8" />
                  <stop offset="100%" stopColor="#16A34A" />
                </linearGradient>
              </defs>
            </svg>
            <span className="absolute top-2 left-3 w-2.5 h-2.5 rotate-45 rounded-[2px] bg-[#B059E8]/70" />
            <span className="absolute top-6 left-9 w-1.5 h-1.5 rotate-45 rounded-[1px] bg-[#B059E8]/40" />
            <span className="absolute top-3 right-6 w-3 h-3 rotate-45 rounded-[2px] bg-[#B059E8]/60" />
            <span className="absolute top-9 right-4 w-2 h-2 rotate-45 rounded-[1px] bg-[#B059E8]/35" />
            <span className="absolute top-1.5 right-20 w-1.5 h-1.5 rotate-45 rounded-[1px] bg-[#B059E8]/30" />

            <div className="relative flex items-center justify-center gap-2.5">
              <span className="relative shrink-0 inline-flex items-center justify-center w-6 h-6">
                {/* Rain DAİMA site parası dağıtır (D3) → Robux logosu. */}
                <RobuxIcon size={34} />
              </span>
              <OdometerAmount value={rainPrize} className="text-white text-[34px] font-black leading-none tracking-tight" decimalClassName="text-[18px] text-zinc-300 ml-0.5" />
            </div>

            <div className="relative z-[1] flex items-center justify-center gap-2.5 mt-2.5">
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/30 border border-[#B059E8]/25 text-[11px] font-bold text-zinc-200">
                <Users className="w-3.5 h-3.5 text-[#c98bf0]" /> {rainParticipants} <span className="text-zinc-500 font-medium">joined</span>
              </span>
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/30 border border-[#B059E8]/25">
                <Timer className={`w-3.5 h-3.5 ${rainTimeLeft > 0 ? 'text-[#c98bf0] animate-pulse' : 'text-zinc-500'}`} />
                <span className={`text-[11px] mm-num font-black tracking-wider ${rainTimeLeft > 0 ? 'text-[#c98bf0]' : 'text-white'}`}>{formatRainTime(rainTimeLeft)}</span>
              </span>
            </div>
            <p className="relative text-center text-[#c98bf0] font-black text-base mt-2 tracking-wide">It's about to RAIN!</p>
            <p className="relative text-center text-zinc-400 text-xs mt-0.5 mb-3.5">Complete the captcha to join the rain!</p>

            <div className="relative flex items-center justify-center gap-2">
              {hasJoinedRain ? (
                <span className="flex items-center gap-1.5 px-6 py-2.5 rounded-lg bg-[#16A34A]/15 border border-[#16A34A]/40 text-[#2ecf6e] text-sm font-black uppercase tracking-wider">
                  <ShieldCheck className="w-4 h-4" /> Joined
                </span>
              ) : (
                <button
                  onClick={() => { if (!isAuthenticated) { toast.error('Log in to join the rain!'); return; } setIsJoinModalOpen(true); }}
                  className="px-7 py-2.5 rounded-lg bg-[#16A34A] hover:bg-[#15913f] text-white text-sm font-black tracking-wide shadow-[0_0_16px_rgba(22,163,74,0.5)] transition-all hover:scale-[1.03]"
                >
                  Join Rain
                </button>
              )}
              <button
                onClick={() => { if (!isAuthenticated) { toast.error('Log in to tip!'); return; } setIsTipModalOpen(true); }}
                title="Tip to Rain Pool"
                className="w-11 h-11 flex items-center justify-center rounded-lg bg-[#B059E8] hover:brightness-110 text-white shadow-[0_0_14px_rgba(176,89,232,0.5)] transition-all shrink-0"
              >
                <Droplet className="w-5 h-5" />
              </button>
            </div>

            <div className="absolute bottom-0 inset-x-0 h-[3px] bg-black/40">
              <div
                className="h-full bg-gradient-to-r from-[#B059E8] via-[#a855f7] to-[#16A34A] shadow-[0_0_10px_rgba(176,89,232,0.7)] transition-[width] duration-1000 ease-linear"
                style={{ width: `${Math.max(0, Math.min(100, (rainTimeLeft / (rainDuration || 1800)) * 100))}%` }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="relative flex-shrink-0 overflow-hidden z-30 px-2 pt-2">
          <div
            className="relative rounded-xl border border-[#2A2D31] overflow-hidden"
            style={{ background: 'linear-gradient(180deg, #26242c 0%, #1d1b22 100%)' }}
          >
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 8% 50%, rgba(176,89,232,0.16) 0%, transparent 55%)' }} />

            <div className="relative flex items-center justify-between px-3 py-2.5 gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="relative shrink-0 inline-flex items-center justify-center w-5 h-5">
                  <RobuxIcon size={22} />
                </span>
                <div className="flex flex-col leading-none">
                  <OdometerAmount value={rainPrize} className="font-black text-white text-[17px] leading-none tracking-tight" decimalClassName="text-[11px] text-zinc-400" />
                  <span className="text-[9px] font-black text-[#B059E8] uppercase tracking-[0.22em] leading-none mt-1.5">Rain Pool</span>
                </div>
              </div>

              {/* Sag grup: sayac (katilimci + timer) + butonlar birlikte, saga yaslanir */}
              <div className="flex items-center gap-2 shrink-0">
                {/* Katilimci sayisi (sadece rain aktifken) + sayisal timer (her zaman) */}
                <div className="flex items-center gap-2 shrink-0">
                {rainActive && (
                  <span className="flex items-center gap-1 text-[11px] font-bold text-zinc-400">
                    <Users className="w-3.5 h-3.5 text-zinc-500" /> {rainParticipants}
                  </span>
                )}
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/25 border border-[#B059E8]/20">
                  <Timer className={`w-3 h-3 ${rainTimeLeft > 0 ? 'text-[#c98bf0] animate-pulse' : 'text-zinc-500'}`} />
                  <span className={`text-[10px] mm-num font-black tracking-wider ${rainTimeLeft > 0 ? 'text-[#c98bf0]' : 'text-white'}`}>{formatRainTime(rainTimeLeft)}</span>
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {rainActive ? (
                  <>
                    <button
                      onClick={() => { if (!isAuthenticated) { toast.error('Log in to tip!'); return; } setIsTipModalOpen(true); }}
                      title="Tip to Rain Pool"
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#15171a] border border-[#2A2D31] text-zinc-300 hover:text-[#B059E8] hover:border-[#B059E8]/40 transition-colors shrink-0"
                    >
                      <Droplet className="w-4 h-4" />
                    </button>
                    {hasJoinedRain ? (
                      <span className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#16A34A]/15 border border-[#16A34A]/40 text-[#2ecf6e] text-xs font-black uppercase tracking-wider shrink-0">
                        <ShieldCheck className="w-3.5 h-3.5" /> Joined
                      </span>
                    ) : (
                      <button
                        onClick={() => { if (!isAuthenticated) { toast.error('Log in to join the rain!'); return; } setIsJoinModalOpen(true); }}
                        className="px-4 py-2 rounded-lg bg-[#16A34A] hover:bg-[#15913f] text-white text-xs font-black tracking-wide shadow-[0_0_14px_rgba(22,163,74,0.45)] transition-all hover:scale-[1.03] shrink-0"
                      >
                        Join
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => { if (!isAuthenticated) { toast.error('Log in to tip!'); return; } setIsTipModalOpen(true); }}
                    title="Tip to Rain Pool"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-[#15171a] border border-[#2A2D31] text-zinc-300 hover:text-[#B059E8] hover:border-[#B059E8]/40 transition-colors shrink-0"
                  >
                    <Droplet className="w-4 h-4" />
                  </button>
                )}
              </div>
              </div>
            </div>

            <div className="absolute bottom-0 inset-x-0 h-[3px] bg-black/40">
              <div
                className="h-full bg-gradient-to-r from-[#B059E8] via-[#a855f7] to-[#16A34A] shadow-[0_0_10px_rgba(176,89,232,0.7)] transition-[width] duration-1000 ease-linear"
                style={{ width: `${Math.max(0, Math.min(100, (rainTimeLeft / (rainDuration || 1800)) * 100))}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex-shrink-0 h-3 -mt-px pointer-events-none z-20 bg-gradient-to-b from-[#211f27] to-transparent" />

      {/* ========================================== */}
      {/* 📌 SABİTLENMİŞ SİSTEM MESAJI (PIN)            */}
      {/* Admin -> /systemmessage "metin" ile ayarlar  */}
      {/* Site açıldığında AÇIK gelir, pin'e basınca    */}
      {/* kapanır.                                      */}
      {/* ========================================== */}
      {systemPin && (
        <div className="relative flex-shrink-0 z-20 px-2 pb-1">
          <div className="rounded-xl bg-[#26232e] border border-white/[0.05]">
            {/* Başlık satırı (tıklanınca aç/kapa) */}
            <button
              type="button"
              onClick={() => setSystemPinOpen(v => !v)}
              className={`w-full flex items-center justify-between px-4 select-none group/pin ${systemPinOpen ? 'pt-3 pb-1' : 'py-3'}`}
              title={systemPinOpen ? 'Hide system message' : 'Show system message'}
              aria-expanded={systemPinOpen}
            >
              <span className="text-[15px] font-bold tracking-wide text-[#B059E8] leading-none">System</span>
              <span className="text-[#B059E8] opacity-70 group-hover/pin:opacity-100 transition-opacity">
                {systemPinOpen
                  ? <Pin className="w-4 h-4" style={{ fill: 'currentColor', transform: 'rotate(-40deg)' }} />
                  : <PinOff className="w-4 h-4" />}
              </span>
            </button>

            {/* Mesaj içeriği */}
            <AnimatePresence initial={false}>
              {systemPinOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <p className="px-4 pb-3 text-[15px] leading-snug text-zinc-300 break-words whitespace-pre-wrap">
                    {renderEmotesInPart(systemPin, 'pin')}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      <AnimatePresence>
        {personalRainWin && (
            <motion.div
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                className="mx-4 overflow-hidden z-10 relative flex-shrink-0"
            >
                <div className="p-3 rounded-xl bg-gradient-to-r from-green-500/20 to-teal-500/20 border border-green-500/30 flex items-center gap-3 shadow-[0_0_20px_rgba(22, 163, 74,0.2)]">
                    <div className="p-2 bg-green-500/20 rounded-full flex-shrink-0">
                        <Droplet className="w-4 h-4 text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-green-400 font-bold text-[10px] uppercase tracking-widest truncate">Rain Ended!</p>
                        <p className="text-white mm-num font-bold text-sm truncate">
                            You won <span className="text-green-400">{formatAmount(personalRainWin)}<RobuxIcon size={12} className="ml-0.5" /></span> from rain!
                        </p>
                    </div>
                    <button onClick={() => setPersonalRainWin(null)} className="flex-shrink-0 text-green-400/50 hover:text-green-400">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </motion.div>
        )}
      </AnimatePresence>

      {/* Messages */}
      <div ref={chatContainerRef} onScroll={handleChatScroll} className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin relative z-0" data-testid="chat-messages">
        <AnimatePresence>
          {visibleMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-10">
              <span className="text-4xl mb-3">💬</span>
              <p className="text-zinc-500 text-sm">No messages yet</p>
              <p className="text-zinc-600 text-xs mt-1">Be the first to send a message!</p>
            </div>
          ) : (
            visibleMessages.map((msg, index) => {
              const myUsername = user?.site_username || user?.username || '';
              
              let isMentioned = false;
              if (myUsername && msg.user_id !== 'system') {
                  const words = msg.message.split(/\s+/);
                  isMentioned = words.some(word => 
                      word.replace(/[.,!?]+$/g, '').toLowerCase() === `@${myUsername.toLowerCase()}`
                  );
              }

              const isTnt = Number(msg.level) >= 150;

              const isSelf = normalizeName(msg.username) === normalizeName(myUsername);
              const muted = msg.user_id !== 'system' && !isSelf && isMuted(msg.username);

              // ── MUTELI KULLANICI: kimlik maskelenir, tiklayinca gercek profil acilir ──
              if (muted) {
                return (
                  <motion.div
                    key={msg.id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="group"
                  >
                    <div className="flex items-start gap-3 p-3 rounded-xl border border-transparent hover:bg-white/[0.03] transition-all opacity-60 hover:opacity-100">
                      <div
                        onClick={() => handleUserClick(msg.username)}
                        title="Muted user — click to view profile"
                        className="relative z-10 w-9 h-9 rounded-lg border border-[#3a2326] bg-[#1a0d0d] flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden cursor-pointer transition-all hover:border-rose-500/40"
                      >
                        <img
                          src={MUTED_AVATAR}
                          alt="Muted"
                          style={{ imageRendering: 'pixelated' }}
                          className="w-full h-full object-contain p-0.5 opacity-80"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>

                      <div className="flex-1 min-w-0 relative z-10">
                        <div
                          className="flex items-center gap-2 mb-1 cursor-pointer group/name"
                          onClick={() => handleUserClick(msg.username)}
                        >
                          <span className="font-semibold text-sm text-zinc-500 group-hover/name:text-zinc-300 transition-colors truncate max-w-[140px]">
                            Muted User
                          </span>
                          <span className="text-[9px] px-2 py-0.5 rounded-full font-black tracking-wider uppercase bg-[#26181a] text-rose-400/70 border border-rose-500/20 flex items-center gap-1 flex-shrink-0">
                            <VolumeX className="w-2.5 h-2.5" /> Muted
                          </span>
                          <span className="text-[10px] text-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity ml-auto flex-shrink-0">
                            {formatMessageTime(msg.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed text-zinc-600 mm-num select-none">#</p>
                      </div>
                    </div>
                  </motion.div>
                );
              }

              return (
              <motion.div 
                key={msg.id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="group"
              >
                <div className={`chat-msg flex items-start gap-3 p-3 rounded-xl transition-all ${
                  isTnt
                    ? 'tnt-message-bg relative overflow-hidden'
                    : (isMentioned 
                        ? 'bg-purple-500/10 border border-purple-500/30 shadow-[0_0_20px_rgba(176, 89, 232,0.1)]' 
                        : (msg.level >= 120 ? 'gold-message-bg' : 'hover:bg-white/5 border border-transparent'))
                }`}>

                  {isTnt && (
                    <div className="tnt-decor" aria-hidden="true">
                      <span className="tnt-float tnt-f1"><img src="/coin.png" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} /></span>
                      <span className="tnt-float tnt-f2 ccw"><img src="/coin.png" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} /></span>
                      <span className="tnt-float tnt-f3"><img src="/coin.png" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} /></span>
                      <span className="tnt-float tnt-f4 ccw"><img src="/coin.png" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} /></span>
                      <span className="tnt-float tnt-f5"><img src="/coin.png" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} /></span>
                    </div>
                  )}
                  
                  {/* ⚠️ ARTIK `has_roblox_account` BAYRAĞINA GÜVENMİYORUZ.
                      Bayrak yanlış olduğunda (mesaj gönderim anında donması,
                      eski kayıtlarda hiç bulunmaması, WS ve REST yollarının
                      farklı davranması) resim HİÇ DENENMİYORDU ve kullanıcı
                      avatarını asla göremiyordu. Bayrak bir OPTİMİZASYONDU,
                      doğruluk kaynağı değil — o rolü kaldırdık.
                      Yeni kural basit ve kendi kendini düzeltir:
                        resim yüklenirse göster, yüklenmezse baş harfi çiz.
                      Roblox'u olmayan kullanıcı için sunucu 404 + 300sn cache
                      döndüğü için maliyet kullanıcı başına tek istektir. */}
                  {!failedAvatars.has(msg.username) ? (
                    <div 
                      onClick={() => handleUserClick(msg.username)}
                      className={`relative z-10 w-9 h-9 rounded-full border border-[#2A2D31] bg-[#121417] flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden cursor-pointer transition-colors ${isTnt ? 'tnt-avatar-border' : (msg.level >= 120 ? 'gold-avatar-border' : (msg.level >= 100 ? 'fire-avatar-border' : 'hover:border-purple-500/50'))}`}
                    >
                      {/* ⚠️ Eskiden hata durumunda HERKESE AYNI `/default-avatar.png`
                          gösteriliyordu: farklı kişiler aynı görselle çıkıyor,
                          sohbette kim kim belli olmuyordu. Baş harfe düşülür —
                          renk kullanıcı adından türediği için ayırt edilebilir. */}
                      <img 
                        src={avatarUrl(msg.username)} 
                        alt="Avatar" 
                        className="w-full h-full object-cover"
                        onError={() => setFailedAvatars(prev => {
                          if (prev.has(msg.username)) return prev;
                          const next = new Set(prev);
                          next.add(msg.username);
                          return next;
                        })}
                      />
                    </div>
                  ) : (
                    <div 
                      onClick={() => handleUserClick(msg.username)}
                      className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg cursor-pointer transition-all ${isTnt ? 'tnt-badge tnt-avatar-border' : (msg.level >= 120 ? 'gold-badge gold-avatar-border' : (msg.level >= 100 ? 'fire-badge fire-avatar-border' : `bg-gradient-to-br ${getAvatarColor(msg.username)} hover:opacity-80`))}`}
                    >
                      <span className="text-white text-sm font-bold">{msg.username?.charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0 relative z-10">
                    <div 
                      className="flex items-center gap-2 mb-1 cursor-pointer group/name"
                      onClick={() => handleUserClick(msg.username)}
                    >
                      <span className={`font-semibold text-sm truncate max-w-[120px] transition-colors ${
                        msg.user_id === 'system' 
                          ? 'text-purple-400 drop-shadow-md' 
                          : (isTnt ? 'tnt-text' : (msg.level >= 120 ? 'gold-text' : (msg.level >= 100 ? 'fire-text' : (msg.is_admin ? 'text-rose-400 group-hover/name:text-rose-300' : 'text-white group-hover/name:text-purple-400'))))
                      }`}>
                        {msg.username}
                      </span>

                      {msg.user_id !== 'system' && (
                        <div className="flex items-center gap-1">
                          {msg.is_admin && (
                            <div className="relative group/badge flex items-center justify-center">
                              <ShieldCheck className="w-4 h-4 text-blue-400 drop-shadow-[0_0_8px_rgba(96,165,250,0.6)]" />
                              <span className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 scale-0 group-hover/badge:scale-100 transition-transform origin-bottom px-2 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[9px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">Admin</span>
                            </div>
                          )}
                          {msg.is_moderator && (
                            <div className="relative group/badge flex items-center justify-center">
                              <Shield className="w-4 h-4 text-green-400 drop-shadow-[0_0_8px_rgba(34, 197, 94,0.6)]" />
                              <span className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 scale-0 group-hover/badge:scale-100 transition-transform origin-bottom px-2 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[9px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">Moderator</span>
                            </div>
                          )}
                          {msg.is_streamer && (
                            <div className="relative group/badge flex items-center justify-center">
                              <Tv className="w-4 h-4 text-purple-400 drop-shadow-[0_0_5px_rgba(168,85,247,0.5)]" />
                              <span className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 scale-0 group-hover/badge:scale-100 transition-transform origin-bottom px-2 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[9px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">Streamer</span>
                            </div>
                          )}
                          {msg.is_youtuber && (
                            <div className="relative group/badge flex items-center justify-center">
                              <Youtube className="w-4 h-4 text-red-500 drop-shadow-[0_0_5px_rgba(239,68,68,0.5)]" />
                              <span className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 scale-0 group-hover/badge:scale-100 transition-transform origin-bottom px-2 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[9px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">YouTuber</span>
                            </div>
                          )}
                          {msg.is_tiktoker && (
                            <div className="relative group/badge flex items-center justify-center">
                              <Music className="w-3.5 h-3.5 text-[#25F4EE] drop-shadow-[1.2px_1.2px_0px_#FE2C55]" />
                              <span className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 scale-0 group-hover/badge:scale-100 transition-transform origin-bottom px-2 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[9px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">TikToker</span>
                            </div>
                          )}
                          {msg.is_vip && (
                            <div className="relative group/badge flex items-center justify-center">
                              <Crown className="w-3.5 h-3.5 text-amber-400 drop-shadow-[0_0_5px_rgba(251,191,36,0.5)]" />
                              <span className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 scale-0 group-hover/badge:scale-100 transition-transform origin-bottom px-2 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[9px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">VIP</span>
                            </div>
                          )}
                          {Number(msg.level) >= 80 && (
                            <div className="relative group/badge flex items-center justify-center">
                              <Gem className="w-3.5 h-3.5 highroller-gem" />
                              <span className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 scale-0 group-hover/badge:scale-100 transition-transform origin-bottom px-2 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[9px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">High Roller</span>
                            </div>
                          )}
                        </div>
                      )}

                      {msg.user_id !== 'system' && (
                          <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold shadow-sm flex-shrink-0 ${getLevelBadge(msg.level).bg}`}>
                            {getLevelBadge(msg.level).text}
                          </span>
                      )}

                      <span className="text-[10px] text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity ml-auto flex-shrink-0">
                        {formatMessageTime(msg.timestamp)}
                      </span>
                    </div>

                    <p className={`text-sm break-words leading-relaxed ${
                      msg.user_id === 'system' 
                        ? 'text-yellow-400 font-medium' 
                        : (isTnt ? 'tnt-text drop-shadow-sm' : (msg.level >= 120 ? 'gold-text drop-shadow-sm' : (msg.level >= 100 ? 'fire-text' : 'text-zinc-300')))
                    }`}>
                      {isMentioned ? (
                        msg.message.split(/(\s+)/).map((part, i) => {
                          if (!part.trim()) return part; 
                          
                          const cleanWord = part.replace(/[.,!?]+$/g, '').toLowerCase();
                          if (cleanWord === `@${myUsername.toLowerCase()}`) {
                            return (
                              <span key={i} className="text-purple-400 font-black bg-purple-500/20 px-1.5 py-0.5 rounded-md border border-purple-500/30 shadow-[0_0_10px_rgba(176, 89, 232,0.2)]">
                                {part}
                              </span>
                            );
                          }
                          return <React.Fragment key={i}>{renderEmotesInPart(part, `m-${i}`)}</React.Fragment>;
                        })
                      ) : (
                        renderEmotesInPart(msg.message, 'msg')
                      )}
                    </p>
                  </div>
                </div>
              </motion.div>
              );
            })
          )}
        </AnimatePresence>
      </div>

      {/* Input */}
      <div className="p-4 border-t border-[#2A2D31] bg-[#1f2226]/80 backdrop-blur z-20 relative flex-shrink-0">

        {/* Emote Picker Panel */}
        <AnimatePresence>
          {showPicker && (
            <motion.div
              ref={pickerRef}
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              className="absolute bottom-full right-4 mb-2 w-[340px] max-w-[calc(100%-2rem)] origin-bottom-right bg-[#1A1D20] border border-[#2A2D31] rounded-2xl shadow-2xl overflow-hidden z-50"
            >
              <div className="px-3 py-2.5 border-b border-[#2A2D31] bg-[#1f2226] flex items-center gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
                  <input
                    type="text"
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    placeholder="Search emotes..."
                    className="w-full bg-[#1A1D20] border border-[#2A2D31] rounded-lg pl-8 pr-2 py-1.5 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/50"
                    autoFocus
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { setShowPicker(false); setPickerSearch(''); }}
                  className="p-1 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
                  aria-label="Close picker"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-2 max-h-[280px] overflow-y-auto scrollbar-thin">
                {EMOTE_NAMES.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                    <span className="text-3xl mb-2">🎨</span>
                    <p className="text-zinc-400 text-sm font-semibold">No emotes yet</p>
                    <p className="text-zinc-600 text-xs mt-1">
                      Add emote names to <code className="px-1 py-0.5 bg-white/5 rounded">EMOTE_NAMES</code>
                      <br />and place images at <code className="px-1 py-0.5 bg-white/5 rounded">public/emotes/</code>
                    </p>
                  </div>
                ) : filteredEmotes.length === 0 ? (
                  <div className="py-10 text-center">
                    <p className="text-zinc-500 text-sm">No emotes found</p>
                    <p className="text-zinc-600 text-xs mt-1">Try a different search</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-7 gap-0.5">
                    {filteredEmotes.map((name) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => insertEmote(name)}
                        className="aspect-square flex items-center justify-center rounded-lg hover:bg-white/10 active:scale-95 transition-all p-1 group/emote relative"
                        title={name}
                      >
                        <img
                          src={getEmoteUrl(name)}
                          alt={name}
                          className="max-w-full max-h-full object-contain group-hover/emote:scale-110 transition-transform"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.opacity = '0.25'; }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {filteredEmotes.length > 0 && (
                <div className="px-3 py-1.5 border-t border-[#2A2D31] bg-[#121417]">
                  <p className="text-[10px] text-zinc-600 text-center">
                    Type <code className="px-1 py-0.5 bg-white/5 rounded text-zinc-500">:EmoteName:</code> in chat to use
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {isAuthenticated ? (
          <form onSubmit={sendMessage} className="flex gap-3">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={isChatStopped && !user?.is_admin && !user?.is_moderator ? "Chat is paused by a moderator." : "Write your message..."}
                maxLength={200}
                disabled={sending || (isChatStopped && !user?.is_admin && !user?.is_moderator)}
                className="w-full bg-[#121417] border border-[#2A2D31] rounded-xl pl-4 pr-24 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#B059E8]/60 transition-colors disabled:opacity-50"
              />
              <button
                ref={pickerButtonRef}
                type="button"
                onClick={() => setShowPicker(v => !v)}
                disabled={isChatStopped && !user?.is_admin && !user?.is_moderator}
                className={`absolute right-14 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                  showPicker
                    ? 'text-[#c98bf0] bg-[#B059E8]/12'
                    : 'text-zinc-500 hover:text-[#c98bf0] hover:bg-white/[0.06]'
                }`}
                aria-label="Open emote picker"
              >
                <Smile className="w-5 h-5" />
              </button>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-600 mm-num">{newMessage.length}/200</span>
            </div>
            <button
              type="submit"
              disabled={!newMessage.trim() || sending || (isChatStopped && !user?.is_admin && !user?.is_moderator)}
              className="mm-focus px-4 py-3 bg-[#7b2fc4] hover:bg-[#B059E8] disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-white transition-colors flex-shrink-0"
            >
              {sending ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </form>
        ) : (
          <div className="text-center py-2">
            <p className="text-sm text-zinc-500">
              To send a message <a href="/login" className="text-[#c98bf0] hover:text-[#d9a8f5] font-semibold underline-offset-2 hover:underline">log in</a>
            </p>
          </div>
        )}
      </div>

      <div className="relative px-3 py-2.5 border-t border-[#2A2D31] bg-[#1f2226]/80 backdrop-blur flex items-center justify-between flex-shrink-0 z-20">

        <AnimatePresence>
          {showRules && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              className="absolute bottom-full left-2 mb-2 w-[300px] max-w-[calc(100%-1rem)] bg-[#1A1D20] border border-[#2A2D31] rounded-2xl shadow-2xl overflow-hidden z-50"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2D31] bg-[#1f2226]">
                <h3 className="font-heading font-black text-white text-base tracking-wide">Chat Rules</h3>
                <button onClick={() => setShowRules(false)} className="text-zinc-500 hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <ul className="p-4 space-y-2.5 text-sm text-zinc-400 leading-snug">
                <li className="flex gap-2"><span className="text-[#B059E8] mt-0.5">•</span> No racism or harassment of any type is allowed.</li>
                <li className="flex gap-2"><span className="text-[#B059E8] mt-0.5">•</span> Begging is not allowed.</li>
                <li className="flex gap-2"><span className="text-[#B059E8] mt-0.5">•</span> Spamming is prohibited and will result in a mute.</li>
                <li className="flex gap-2"><span className="text-[#B059E8] mt-0.5">•</span> If you experience technical issues, <a href="https://discord.gg/bloxbets" target="_blank" rel="noopener noreferrer" className="text-[#c98bf0] underline underline-offset-2 hover:text-[#d8b8f0]">contact us via support chat</a>.</li>
                <li className="flex gap-2"><span className="text-[#B059E8] mt-0.5">•</span> No affiliate code, custom cases, or socials promotion in chat.</li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center gap-2">
          <a
            href="https://discord.gg/bloxbets"
            target="_blank"
            rel="noopener noreferrer"
            title="Join our Discord"
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#2A2D31]/60 border border-[#2A2D31] text-zinc-400 hover:text-[#5865F2] hover:border-[#5865F2]/40 transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
            </svg>
          </a>
          <button
            onClick={() => setShowRules(v => !v)}
            title="Chat Rules"
            className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
              showRules
                ? 'bg-[#B059E8]/15 border-[#B059E8]/50 text-[#c98bf0]'
                : 'bg-[#2A2D31]/60 border-[#2A2D31] text-zinc-400 hover:text-[#c98bf0] hover:border-[#B059E8]/40'
            }`}
          >
            <Gavel className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-zinc-400 text-xs font-bold">
            <span className="text-sm leading-none">🇺🇸</span> ENG
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${connected ? 'bg-[#2ecf6e] animate-pulse' : 'bg-[#F5A524]'}`} />
            <span className={`text-xs mm-num font-bold ${connected ? 'text-[#2ecf6e]' : 'text-[#F5A524]'}`}>{onlineCount}</span>
          </div>
        </div>
      </div>

      {typeof document !== 'undefined' && ReactDOM.createPortal(
        <AnimatePresence>
          {isJoinModalOpen && (
            <div
              className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsJoinModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 10 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 10 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#1A1D20] border border-[#2A2D31] rounded-2xl p-6 w-full max-w-xs shadow-2xl relative overflow-hidden"
              >
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-[#B059E8] to-[#16A34A]" />
                <button
                  onClick={() => setIsJoinModalOpen(false)}
                  className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#16A34A]/10 border border-[#16A34A]/30 flex items-center justify-center">
                    <Droplet className="w-5 h-5 text-[#2ecf6e]" />
                  </div>
                  <div>
                    <h3 className="text-white font-black uppercase tracking-widest text-base leading-tight">Join Rain</h3>
                    <p className="text-xs text-zinc-400">Verify & claim your share</p>
                  </div>
                </div>

                <div className="flex justify-center mb-5">
                  <HCaptcha
                    sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY || "KENDI_HCAPTCHA_SITE_KEYINI_BURAYA_YAZ"}
                    onVerify={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                    theme="dark"
                  />
                </div>

                <button
                  onClick={handleJoinRain}
                  disabled={isJoining || !captchaToken}
                  className={`w-full py-3 rounded-xl font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
                    isJoining || !captchaToken
                      ? 'bg-[#2A2D31]/50 text-zinc-500 cursor-not-allowed'
                      : 'bg-[#16A34A] hover:bg-[#15913f] text-white shadow-[0_0_20px_rgba(22,163,74,0.3)]'
                  }`}
                >
                  {isJoining ? <><RefreshCw className="w-4 h-4 animate-spin" /> Joining...</> : 'Join Rain'}
                </button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {typeof document !== 'undefined' && ReactDOM.createPortal(
        <AnimatePresence>
          {isTipModalOpen && (
            <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setIsTipModalOpen(false)}>
              <motion.div 
                initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#1A1D20] border border-purple-500/30 rounded-2xl p-6 w-full max-w-sm shadow-[0_0_40px_rgba(176, 89, 232,0.15)] relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-purple-500" />
                <button onClick={() => setIsTipModalOpen(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center border border-purple-500/30">
                    <Droplet className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-white uppercase tracking-widest">Tip to Rain</h3>
                    <p className="text-xs text-zinc-400">Increase the global rain pool!</p>
                  </div>
                </div>

                <form onSubmit={handleTipSubmit}>
                  <div className="mb-6">
                    <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 block">Amount ($)</label>
                    <div className="relative">
                      <input 
                        type="text" 
                        value={tipAmount} 
                        onChange={(e) => setTipAmount(e.target.value)} 
                        placeholder="e.g. 100k, 1.5m"
                        className="w-full bg-[#121417] border border-[#2A2D31] rounded-xl px-4 py-3 text-white mm-num focus:border-purple-500 outline-none transition-colors pr-12"
                      />
                      {/* Rain havuzu DAİMA site parası (D3) — Robux simgesi. */}
                      <span className="absolute right-4 top-1/2 -translate-y-1/2"><RobuxIcon size={15} /></span>
                    </div>
                  </div>
                  <button 
                    type="submit" 
                    disabled={isTipping}
                    className={`w-full py-3 rounded-xl font-black uppercase tracking-widest transition-all ${isTipping ? 'bg-[#2A2D31]/50 text-zinc-500 cursor-not-allowed' : 'bg-gradient-to-r from-purple-500 to-purple-500 hover:from-purple-400 hover:to-purple-400 text-white shadow-lg shadow-purple-500/25'}`}
                  >
                    {isTipping ? 'Processing...' : 'Send Tip'}
                  </button>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {typeof document !== 'undefined' && ReactDOM.createPortal(
        <AnimatePresence>
          {selectedProfile && (
            <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/90" onClick={() => setSelectedProfile(null)}>
              <motion.div 
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md relative mt-12"
              >
                <div className="absolute inset-0 bg-[#121417] rounded-3xl border border-[#2A2D31] shadow-[0_20px_70px_rgba(0,0,0,0.8)] overflow-hidden">
                   <div className="absolute -top-32 -left-32 w-80 h-80 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-green-500/10 to-transparent rounded-full" />
                   <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-purple-500/10 to-transparent rounded-full" />
                   <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-[0.08]" />
                </div>

                <div className="relative z-10">
                  <div className="h-32 bg-gradient-to-r from-green-900/40 via-[#1A1D20] to-purple-900/40 border-b border-[#2A2D31] rounded-t-3xl relative">
                    <button onClick={() => setSelectedProfile(null)} className="absolute top-4 right-4 p-2 bg-black/40 rounded-full text-zinc-400 hover:text-white transition-colors z-10">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="px-6 pb-8 relative flex flex-col items-center">
                      <div className="-mt-16 mb-4 relative z-10">
                        {!failedAvatars.has(selectedProfile.username) ? (
                          <img 
                            src={avatarUrl(selectedProfile.username)} 
                            alt="Avatar" 
                            className={`relative w-28 h-28 rounded-full border-[4px] border-[#1A1D20] bg-[#121417] object-cover ${selectedProfile.level >= 150 ? 'tnt-avatar-border' : (selectedProfile.level >= 120 ? 'gold-avatar-border' : (selectedProfile.level >= 100 ? 'fire-avatar-border' : 'shadow-[0_0_30px_rgba(22, 163, 74,0.3)]'))}`}
                            onError={() => setFailedAvatars(prev => {
                              if (prev.has(selectedProfile.username)) return prev;
                              const next = new Set(prev);
                              next.add(selectedProfile.username);
                              return next;
                            })}
                          />
                        ) : (
                          <div className={`relative w-28 h-28 rounded-full border-[4px] border-[#1A1D20] flex items-center justify-center ${selectedProfile.level >= 150 ? 'tnt-avatar-border tnt-badge' : (selectedProfile.level >= 120 ? 'gold-avatar-border gold-badge' : (selectedProfile.level >= 100 ? 'fire-avatar-border fire-badge' : `bg-gradient-to-br ${getAvatarColor(selectedProfile.username)} shadow-[0_0_30px_rgba(176, 89, 232,0.3)]`))}`}>
                            <span className="text-white text-5xl font-bold">{selectedProfile.username?.charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                        
                        {selectedProfile.is_private && !selectedProfile.loading && (
                           <div className="absolute bottom-0 right-0 w-8 h-8 bg-rose-500 rounded-full border-[3px] border-[#1A1D20] flex items-center justify-center shadow-lg" title="Private Profile">
                              <Lock className="w-3.5 h-3.5 text-white" />
                           </div>
                        )}
                     </div>
                     
                      <div className="text-center mb-8 w-full">
                        <div className="flex items-center justify-center gap-2 px-4">
                            <h2 className={`text-2xl font-black tracking-wide truncate ${selectedProfile.level >= 150 ? 'tnt-text' : (selectedProfile.level >= 120 ? 'gold-text' : (selectedProfile.level >= 100 ? 'fire-text' : 'text-white'))}`}>
                              {selectedProfile.username}
                           </h2>
                           
                           {!selectedProfile.loading && (
                               <div className="flex items-center gap-1.5">
                                   {selectedProfile.is_admin && (
                                     <div className="relative group/modal flex items-center justify-center">
                                       <ShieldCheck className="w-6 h-6 text-blue-400 drop-shadow-[0_0_10px_rgba(96,165,250,0.6)]" />
                                       <span className="absolute top-full mt-2 left-1/2 -translate-x-1/2 scale-0 group-hover/modal:scale-100 transition-transform origin-top px-3 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[10px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">Admin</span>
                                     </div>
                                   )}
                                   {selectedProfile.is_moderator && (
                                     <div className="relative group/modal flex items-center justify-center">
                                       <Shield className="w-6 h-6 text-green-400 drop-shadow-[0_0_10px_rgba(34, 197, 94,0.6)]" />
                                       <span className="absolute top-full mt-2 left-1/2 -translate-x-1/2 scale-0 group-hover/modal:scale-100 transition-transform origin-top px-3 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[10px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">Moderator</span>
                                     </div>
                                   )}
                                   {selectedProfile.is_streamer && (
                                     <div className="relative group/modal flex items-center justify-center">
                                       <Tv className="w-6 h-6 text-purple-400 drop-shadow-[0_0_8px_rgba(168,85,247,0.6)]" />
                                       <span className="absolute top-full mt-2 left-1/2 -translate-x-1/2 scale-0 group-hover/modal:scale-100 transition-transform origin-top px-3 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[10px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">Streamer</span>
                                     </div>
                                   )}
                                   {selectedProfile.is_youtuber && (
                                     <div className="relative group/modal flex items-center justify-center">
                                       <Youtube className="w-6 h-6 text-red-500 drop-shadow-[0_0_8px_rgba(239,68,68,0.6)]" />
                                       <span className="absolute top-full mt-2 left-1/2 -translate-x-1/2 scale-0 group-hover/modal:scale-100 transition-transform origin-top px-3 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[10px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">YouTuber</span>
                                     </div>
                                   )}
                                   {selectedProfile.is_tiktoker && (
                                     <div className="relative group/modal flex items-center justify-center">
                                       <Music className="w-5 h-5 text-[#25F4EE] drop-shadow-[2px_2px_0px_#FE2C55]" />
                                       <span className="absolute top-full mt-2 left-1/2 -translate-x-1/2 scale-0 group-hover/modal:scale-100 transition-transform origin-top px-3 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[10px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">TikToker</span>
                                     </div>
                                   )}
                                   {selectedProfile.is_vip && (
                                     <div className="relative group/modal flex items-center justify-center">
                                       <Crown className="w-6 h-6 text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]" />
                                       <span className="absolute top-full mt-2 left-1/2 -translate-x-1/2 scale-0 group-hover/modal:scale-100 transition-transform origin-top px-3 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[10px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">VIP</span>
                                     </div>
                                   )}
                                   {Number(selectedProfile.level) >= 80 && (
                                     <div className="relative group/modal flex items-center justify-center">
                                       <Gem className="w-6 h-6 highroller-gem" />
                                       <span className="absolute top-full mt-2 left-1/2 -translate-x-1/2 scale-0 group-hover/modal:scale-100 transition-transform origin-top px-3 py-1 bg-[#1A1D20] border border-[#2A2D31] text-white text-[10px] font-black tracking-wider uppercase rounded-md shadow-xl pointer-events-none z-50 whitespace-nowrap">High Roller</span>
                                     </div>
                                   )}
                               </div>
                           )}
                        </div>

                        <div className="mt-2">
                          <span className={`inline-block px-4 py-1 rounded-full text-xs font-black tracking-widest shadow-sm uppercase ${getLevelBadge(selectedProfile.level).bg}`}>
                            {getLevelBadge(selectedProfile.level).text}
                          </span>
                        </div>
                     </div>

                     <div className="w-full">
                       {selectedProfile.loading ? (
                          <div className="py-8 flex flex-col items-center justify-center">
                            <RefreshCw className="w-8 h-8 text-green-400 animate-spin mb-4" />
                            <span className="text-zinc-400 text-sm">Loading stats...</span>
                          </div>
                       ) : selectedProfile.is_private ? (
                          <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-8 flex flex-col items-center justify-center text-center">
                             <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mb-4 border border-rose-500/20">
                                <Lock className="w-8 h-8 text-rose-500" />
                             </div>
                             <h3 className="text-white font-bold text-lg mb-1">Private Profile</h3>
                             <p className="text-zinc-500 text-sm px-4">This player prefers to keep their casino statistics hidden from the public.</p>
                          </div>
                       ) : (
                          <div className="grid grid-cols-2 gap-3">
                             <div className="bg-[#121417] border border-[#2A2D31] p-4 rounded-2xl hover:border-green-500/50 transition-colors duration-200">
                                <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-1">Total Games</p>
                                <p className="text-white mm-num font-bold text-lg">{selectedProfile.total_games}</p>
                             </div>
                             <div className="bg-[#121417] border border-[#2A2D31] p-4 rounded-2xl hover:border-green-500/50 transition-colors duration-200">
                                <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-1">Win Rate</p>
                                <p className="text-white mm-num font-bold text-lg">{selectedProfile.win_rate}%</p>
                             </div>
                             <div className="bg-[#121417] border border-[#2A2D31] p-4 rounded-2xl hover:border-blue-500/50 transition-colors duration-200">
                                <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-1">Total Wagered</p>
                                <p className="text-blue-400 mm-num font-bold text-lg">{formatCompact(selectedProfile.total_wagered)}<RobuxIcon size={13} className="ml-1" /></p>
                             </div>
                             <div className="bg-[#121417] border border-[#2A2D31] p-4 rounded-2xl hover:border-green-500/50 transition-colors duration-200">
                                <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-1">Net Profit</p>
                                <p className={`mm-num font-bold text-lg ${selectedProfile.net_profit >= 0 ? 'text-green-400' : 'text-rose-400'}`}>
                                   {selectedProfile.net_profit >= 0 ? '+ ' : ''}{formatCompact(selectedProfile.net_profit)}<RobuxIcon size={13} className="ml-1" />
                                </p>
                             </div>
                             <div className="bg-[#121417] border border-[#2A2D31] p-4 rounded-2xl hover:border-green-500/50 transition-colors duration-200">
                                <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-1">All Time High</p>
                                <p className="text-green-400 mm-num font-bold text-lg">{formatCompact(selectedProfile.all_time_high)}<RobuxIcon size={13} className="ml-1" /></p>
                             </div>
                             <div className="bg-[#121417] border border-[#2A2D31] p-4 rounded-2xl hover:border-rose-500/50 transition-colors duration-200">
                                <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-1">Biggest Loss</p>
                                <p className="text-rose-400 mm-num font-bold text-lg">{formatCompact(selectedProfile.all_time_low)}<RobuxIcon size={13} className="ml-1" /></p>
                             </div>
                          </div>
                       )}

                       {/* ── MUTE / UNMUTE AKSIYONU ── */}
                       {!selectedProfile.loading &&
                        selectedProfile.username &&
                        normalizeName(selectedProfile.username) !== normalizeName(myUsername) && (
                         <div className="w-full mt-4">
                           {isMuted(selectedProfile.username) ? (
                             <button
                               onClick={() => { unmute(selectedProfile.username); toast.success(`Unmuted ${selectedProfile.username}`); }}
                               className="group/mute w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-sm bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:border-emerald-500/50 transition-all shadow-[0_0_20px_rgba(16,185,129,0.08)]"
                             >
                               <Volume2 className="w-4 h-4 transition-transform group-hover/mute:scale-110" /> Unmute User
                             </button>
                           ) : (
                             <button
                               onClick={() => { mute(selectedProfile.username); toast(`Muted ${selectedProfile.username}`, { icon: '🔇' }); }}
                               className="group/mute w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-sm bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 hover:border-rose-500/50 transition-all shadow-[0_0_20px_rgba(244,63,94,0.08)]"
                             >
                               <VolumeX className="w-4 h-4 transition-transform group-hover/mute:scale-110" /> Mute User
                             </button>
                           )}
                           <p className="mt-2 text-center text-[10px] text-zinc-600 leading-snug">
                             {isMuted(selectedProfile.username)
                               ? 'Their messages will be shown normally again.'
                               : 'Their messages will be hidden behind a barrier — on this device only.'}
                           </p>
                         </div>
                       )}
                     </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};